import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Inject,
  Input,
  Output,
  TemplateRef,
} from '@angular/core';
import { Direction } from '@rivethealth/data/super-table';
import { from, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { HookDirective } from '../core/hook';
import {
  useEffect,
  useMemo,
  useState,
  useSubscriptionLatest,
} from '../core/hooks';
import { Maybe } from '../helpers/functional';
import {
  CHECKBOX_PX,
  SuperTableContext,
  SUPER_TABLE_CONTEXT_TOKEN,
} from './super-table';
import { TableColumnStore } from './table-column-store.service';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'th[superTableHeader]',
  templateUrl: './super-table-header.component.html',
  styleUrls: ['./super-table-header.component.scss'],
})
export class SuperTableHeaderComponent extends HookDirective {
  constructor(
    @Inject(SUPER_TABLE_CONTEXT_TOKEN)
    private readonly superTableContext: SuperTableContext,
    readonly elementRef: ElementRef,
    private readonly tableColumnStore: TableColumnStore,
    private _cdr: ChangeDetectorRef,
  ) {
    super(_cdr);
  }

  @Input()
  checkbox = false;

  @Input()
  @HostBinding('class.menu')
  menu: boolean = false;

  @Input()
  sort: string | undefined;

  @Input()
  fixedWidth: number;

  @Input()
  minWidth?: number | null;

  @Input()
  defaultWidth?: number | null;

  @Input()
  columnName: string;

  @Input()
  tableName: string;

  @Input()
  canResize: boolean = false;

  @Input()
  help: string;

  @Input()
  @HostBinding('class.simple-header')
  simpleHeader = false;

  @HostBinding('class.ng-dragging')
  isDragging: boolean = false;

  @Input()
  saveState: boolean = true;

  @Input()
  customCheckboxRef: TemplateRef<any> | null = null;

  @Output()
  readonly drag = new EventEmitter();

  readonly direction: Observable<Direction | null> =
    this.superTableContext.effectiveSort.pipe(
      map(
        sort =>
          <Direction | null>(
            (sort && (sort.name === this.sort ? sort.direction : null))
          ),
      ),
    );

  @HostBinding('style.width.px') width: number;

  get _() {
    const nativeElement: Maybe<HTMLTableCellElement> = Maybe.fromValue(
      this.elementRef.nativeElement,
    );
    const innerText: Maybe<string | null> = nativeElement.map(
      el => el.innerText,
    );
    const text = innerText.getOrElse('');

    const hostComputedStyle: Maybe<CSSStyleDeclaration> = nativeElement.map(
      el => window.getComputedStyle(el),
    );

    const container: Maybe<HTMLSpanElement | HTMLButtonElement> =
      nativeElement.map(
        el =>
          <HTMLSpanElement | HTMLButtonElement>(
            el.querySelector('.heading-container')
          ),
      );
    const calculatedTextWidth = useMemo(() => {
      if (text) {
        const borderLeft = hostComputedStyle
          .flatMap(style =>
            style.borderLeftWidth
              ? Maybe.some(style.borderLeftWidth)
              : Maybe.none<string>(),
          )
          .map(value => parseInt(value));
        const borderRight = hostComputedStyle
          .flatMap(style =>
            style.borderRightWidth
              ? Maybe.some(style.borderRightWidth)
              : Maybe.none<string>(),
          )
          .map(value => parseInt(value));

        const iconContainer: Maybe<HTMLSpanElement> = container.map(
          el => <HTMLSpanElement>el.querySelector('.icon-container'),
        );
        const iconSpacing: Maybe<number> = iconContainer.map(el =>
          parseInt(window.getComputedStyle(el).paddingLeft),
        );
        const icon: Maybe<HTMLUnknownElement> = iconContainer.map(
          el => <HTMLUnknownElement>el.querySelector('icon'),
        );
        const iconWidth: Maybe<number> = icon.map(el =>
          parseInt(el.style.width),
        );
        const computedStyle: Maybe<CSSStyleDeclaration> = container.map(el =>
          window.getComputedStyle(el),
        );

        const fontWeight = computedStyle.flatMap(style =>
          style.fontWeight
            ? Maybe.some(style.fontWeight)
            : Maybe.none<string>(),
        );
        const fontSize = computedStyle.flatMap(style =>
          style.fontSize ? Maybe.some(style.fontSize) : Maybe.none<string>(),
        );
        const lineHeight = computedStyle.flatMap(style =>
          style.lineHeight
            ? Maybe.some(style.lineHeight)
            : Maybe.none<string>(),
        );
        const fontFamily = computedStyle.flatMap(style =>
          style.fontFamily
            ? Maybe.some(style.fontFamily)
            : Maybe.none<string>(),
        );
        const font = Maybe.combine([
          fontWeight,
          fontSize,
          lineHeight,
          fontFamily,
        ]).map(
          ([fontWeight, fontSize, lineHeight, fontFamily]) =>
            `${fontWeight} ${fontSize} / ${lineHeight} ${fontFamily}`,
        );

        const paddingLeft = computedStyle
          .flatMap(style =>
            style.paddingLeft
              ? Maybe.some(style.paddingLeft)
              : Maybe.none<string>(),
          )
          .map(value => parseInt(value));
        const paddingRight = computedStyle
          .flatMap(style =>
            style.paddingRight
              ? Maybe.some(style.paddingRight)
              : Maybe.none<string>(),
          )
          .map(value => parseInt(value));
        const letterSpacing = computedStyle
          .flatMap(style =>
            style.letterSpacing
              ? Maybe.some(style.letterSpacing)
              : Maybe.none<string>(),
          )
          .map(value => parseInt(value));

        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d')!;
        context.font = font.getOrElse('600 11px / 11px system-ui');
        const { width: textWidth } = context.measureText(text);

        const calculated = Math.ceil(
          borderLeft.getOrElse(0) +
            paddingLeft.getOrElse(0) +
            textWidth +
            letterSpacing.getOrElse(0) * text.length +
            iconSpacing.getOrElse(0) +
            iconWidth.getOrElse(0) +
            paddingRight.getOrElse(0) +
            borderRight.getOrElse(0),
        );
        // This is for when the all rates page is using codes as rows and creating really large column names
        return calculated > 250 ? 50 : calculated;
      }
      return null;
    }, [text, hostComputedStyle, container]);

    const calculatedWidth = calculatedTextWidth || 50;

    const minWidthOfColumn = this.minWidth
      ? Math.max(this.minWidth, calculatedWidth)
      : calculatedWidth;

    const savedUserWidth = useSubscriptionLatest(
      from(this.tableColumnStore.getWidth(this.tableName, this.columnName)),
      [this.tableName, this.columnName],
    );

    const [userWidth, setUserWidth] = useState<number | null>(null);

    useEffect(() => {
      if (savedUserWidth && userWidth == null) {
        setUserWidth(savedUserWidth);
      }
    }, [savedUserWidth]);

    const currentWidth = this.elementRef.nativeElement.offsetWidth;

    const isConnected = this.elementRef.nativeElement.isConnected;

    const onResize = (width: number) => {
      width = Math.trunc(width);
      setUserWidth(width);
      if (!this.superTableContext) return;
      this.superTableContext.wasResized(this.columnName);
      this._cdr.markForCheck();
    };

    const setWidth = (value: number) => {
      this.width = value;
      this._cdr.markForCheck();
    };

    useEffect(() => {
      if (this.checkbox) {
        setWidth(CHECKBOX_PX);
        return;
      }

      if (this.menu) {
        setWidth(42);
        return;
      }

      if (this.fixedWidth) {
        setWidth(this.width);
        return;
      }

      let newWidth: number | null = null;
      if (currentWidth < minWidthOfColumn) {
        newWidth = minWidthOfColumn;
      }
      if (userWidth && userWidth > minWidthOfColumn) {
        newWidth = userWidth;
      } else if (this.defaultWidth && this.defaultWidth > minWidthOfColumn) {
        newWidth = this.defaultWidth;
      } else {
        newWidth = minWidthOfColumn;
      }
      if (newWidth !== null) {
        setWidth(newWidth);
      }
    }, [
      this.checkbox,
      this.menu,
      minWidthOfColumn,
      userWidth,
      currentWidth,
      isConnected,
    ]);
    return { onResize };
  }

  doSort(direction: Direction | null) {
    this.superTableContext.changeView.next({
      page: 0,
      sortDisabled: false,
    });
    this.superTableContext.changeSort.next(
      direction
        ? {
            name: this.sort!,
            direction,
          }
        : { name: 'default', direction: 'asc' },
    );
  }
}
